import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';
import {
  GraduationCap, Calendar, ClipboardCheck, BookOpen, BarChart3,
  Clock, CheckCircle, XCircle, AlertCircle, ExternalLink, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentDashboard() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/student/my-dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        <Navbar />
        <main className="pt-40 pb-20 px-6">
          <div className="max-w-lg mx-auto card-apple p-8 text-center">
            <GraduationCap className="h-12 w-12 text-[#86868B] mx-auto mb-4" />
            <p className="text-[#86868B]">{language === 'es' ? 'No se encontraron datos del alumno.' : 'No student data found.'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { student, group, attendance, assignments, grades } = data;

  const tabs = [
    { id: 'overview', icon: GraduationCap, label: language === 'es' ? 'Mi clase' : 'My Class' },
    { id: 'schedule', icon: Calendar, label: language === 'es' ? 'Horario' : 'Schedule' },
    { id: 'attendance', icon: ClipboardCheck, label: language === 'es' ? 'Presencia' : 'Attendance' },
    { id: 'assignments', icon: BookOpen, label: language === 'es' ? 'Devoirs' : 'Assignments' },
    { id: 'grades', icon: BarChart3, label: language === 'es' ? 'Notas' : 'Grades' }
  ];

  const attStatusIcon = (status) => {
    if (status === 'present') return <CheckCircle className="h-4 w-4 text-[#34C759]" />;
    if (status === 'absent') return <XCircle className="h-4 w-4 text-[#E63946]" />;
    if (status === 'late') return <Clock className="h-4 w-4 text-[#FF9500]" />;
    return <AlertCircle className="h-4 w-4 text-[#007AFF]" />;
  };

  const attStatusLabel = (status) => {
    const map = {
      present: language === 'es' ? 'Presente' : 'Present',
      absent: language === 'es' ? 'Ausente' : 'Absent',
      late: language === 'es' ? 'Tarde' : 'Late',
      excused: language === 'es' ? 'Justificado' : 'Excused'
    };
    return map[status] || status;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <SEOHead titleES="Mi Dashboard — Colegio ARCE" titleEN="My Dashboard — Colegio ARCE" path="/student" />
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-[#1D1D1F]" data-testid="student-dashboard-title">
              {language === 'es' ? 'Hola' : 'Hello'}, {student.name}
            </h1>
            <p className="text-[#86868B] mt-1">
              {student.level} — {student.school_year}
              {group ? ` — ${group.name}` : ''}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 p-1 bg-white rounded-2xl shadow-sm mb-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#E63946] text-white shadow-md'
                    : 'text-[#86868B] hover:text-[#1D1D1F] hover:bg-[#F5F5F7]'
                }`}
                data-testid={`student-tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="student-overview">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card-apple p-5 text-center">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Nivel' : 'Level'}</p>
                  <p className="text-lg font-bold text-[#007AFF]">{student.level}</p>
                </div>
                <div className="card-apple p-5 text-center">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Presencia' : 'Attendance'}</p>
                  <p className={`text-lg font-bold ${attendance.rate >= 80 ? 'text-[#34C759]' : attendance.rate >= 60 ? 'text-[#FF9500]' : 'text-[#E63946]'}`}>
                    {attendance.rate}%
                  </p>
                </div>
                <div className="card-apple p-5 text-center">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Promedio' : 'Average'}</p>
                  <p className="text-lg font-bold text-[#E63946]">{grades.average != null ? `${grades.average}%` : '—'}</p>
                </div>
                <div className="card-apple p-5 text-center">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Tareas' : 'Assignments'}</p>
                  <p className="text-lg font-bold text-[#1D1D1F]">{assignments.length}</p>
                </div>
              </div>

              {/* Group Info */}
              {group ? (
                <div className="card-apple p-6">
                  <h3 className="text-lg font-semibold text-[#1D1D1F] mb-4">{language === 'es' ? 'Mi clase' : 'My Class'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[#86868B]">{language === 'es' ? 'Grupo' : 'Group'}</p>
                      <p className="font-medium text-[#1D1D1F]">{group.name}</p>
                    </div>
                    <div>
                      <p className="text-[#86868B]">{language === 'es' ? 'Profesor/a' : 'Teacher'}</p>
                      <p className="font-medium text-[#1D1D1F]">{group.teacher_name || '—'}</p>
                    </div>
                    {group.zoom_link && (
                      <div className="sm:col-span-2">
                        <a href={group.zoom_link} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#007AFF]/10 text-[#007AFF] rounded-xl text-sm font-medium hover:bg-[#007AFF]/20 transition-colors"
                          data-testid="student-zoom-link"
                        >
                          <Video className="h-4 w-4" />
                          {language === 'es' ? 'Entrar a clase Zoom' : 'Join Zoom Class'}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card-apple p-8 text-center">
                  <p className="text-[#86868B]">{language === 'es' ? 'No tienes un grupo asignado todavía.' : 'You don\'t have a group assigned yet.'}</p>
                </div>
              )}
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-4" data-testid="student-schedule">
              <h2 className="text-xl font-semibold text-[#1D1D1F]">{language === 'es' ? 'Mi horario' : 'My Schedule'}</h2>
              {group && group.schedule.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {group.schedule.map((slot, i) => (
                    <div key={i} className="card-apple p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                          <Calendar className="h-6 w-6 text-[#007AFF]" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#1D1D1F]">{slot.day}</p>
                          <p className="text-sm text-[#86868B]">{slot.start_time} — {slot.end_time}</p>
                          {slot.room && <p className="text-xs text-[#86868B]">{slot.room}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card-apple p-8 text-center">
                  <p className="text-[#86868B]">{language === 'es' ? 'No hay horario definido todavía.' : 'No schedule defined yet.'}</p>
                </div>
              )}
            </div>
          )}

          {/* Attendance Tab */}
          {activeTab === 'attendance' && (
            <div className="space-y-4" data-testid="student-attendance">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-[#1D1D1F]">{language === 'es' ? 'Mi presencia' : 'My Attendance'}</h2>
                <div className="card-apple px-4 py-2">
                  <span className="text-sm text-[#86868B]">{language === 'es' ? 'Tasa' : 'Rate'}: </span>
                  <span className={`font-bold ${attendance.rate >= 80 ? 'text-[#34C759]' : 'text-[#FF9500]'}`}>{attendance.rate}%</span>
                  <span className="text-xs text-[#86868B] ml-2">({attendance.present}/{attendance.total_sessions})</span>
                </div>
              </div>
              {attendance.recent.length > 0 ? (
                <div className="card-apple overflow-hidden">
                  <table className="w-full" data-testid="student-attendance-table">
                    <thead className="bg-[#F5F5F7]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Fecha' : 'Date'}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-[#86868B]">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Nota' : 'Note'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.recent.map((a, i) => (
                        <tr key={i} className="border-t border-black/5">
                          <td className="px-4 py-3 text-sm text-[#1D1D1F]">{new Date(a.date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1.5">
                              {attStatusIcon(a.status)}
                              <span className="text-xs font-medium">{attStatusLabel(a.status)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#86868B]">{a.note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="card-apple p-8 text-center">
                  <p className="text-[#86868B]">{language === 'es' ? 'No hay registros de presencia.' : 'No attendance records.'}</p>
                </div>
              )}
            </div>
          )}

          {/* Assignments Tab */}
          {activeTab === 'assignments' && (
            <div className="space-y-4" data-testid="student-assignments">
              <h2 className="text-xl font-semibold text-[#1D1D1F]">{language === 'es' ? 'Mis tareas' : 'My Assignments'}</h2>
              {assignments.length > 0 ? (
                <div className="space-y-3">
                  {assignments.map((hw) => {
                    const isPast = hw.due_date && new Date(hw.due_date) < new Date();
                    return (
                      <div key={hw.assignment_id} className={`card-apple p-5 ${isPast ? 'opacity-60' : ''}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-semibold text-[#1D1D1F]">{hw.title}</p>
                            {hw.description && <p className="text-sm text-[#86868B] mt-1">{hw.description}</p>}
                          </div>
                          {hw.due_date && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                              isPast ? 'bg-red-100 text-red-700' : 'bg-[#007AFF]/10 text-[#007AFF]'
                            }`}>
                              {isPast ? (language === 'es' ? 'Vencida' : 'Past due') : new Date(hw.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="card-apple p-8 text-center">
                  <p className="text-[#86868B]">{language === 'es' ? 'No hay tareas asignadas.' : 'No assignments yet.'}</p>
                </div>
              )}
            </div>
          )}

          {/* Grades Tab */}
          {activeTab === 'grades' && (
            <div className="space-y-4" data-testid="student-grades">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-[#1D1D1F]">{language === 'es' ? 'Mis notas' : 'My Grades'}</h2>
                {grades.average != null && (
                  <div className="card-apple px-4 py-2">
                    <span className="text-sm text-[#86868B]">{language === 'es' ? 'Promedio' : 'Average'}: </span>
                    <span className="font-bold text-[#E63946]">{grades.average}%</span>
                  </div>
                )}
              </div>
              {grades.records.length > 0 ? (
                <div className="card-apple overflow-hidden">
                  <table className="w-full" data-testid="student-grades-table">
                    <thead className="bg-[#F5F5F7]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Evaluación' : 'Assessment'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Categoría' : 'Category'}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Nota' : 'Grade'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#86868B]">{language === 'es' ? 'Fecha' : 'Date'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grades.records.map((g) => {
                        const pct = g.max_score > 0 ? Math.round(g.score / g.max_score * 100) : 0;
                        return (
                          <tr key={g.grade_id} className="border-t border-black/5">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-[#1D1D1F]">{g.title}</p>
                              {g.note && <p className="text-xs text-[#86868B]">{g.note}</p>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                g.category === 'exam' ? 'bg-purple-100 text-purple-700' :
                                g.category === 'homework' ? 'bg-blue-100 text-blue-700' :
                                g.category === 'participation' ? 'bg-green-100 text-green-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {g.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-bold ${pct >= 80 ? 'text-[#34C759]' : pct >= 60 ? 'text-[#FF9500]' : 'text-[#E63946]'}`}>
                                {g.score}/{g.max_score}
                              </span>
                              <span className="text-xs text-[#86868B] ml-1">({pct}%)</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-[#86868B]">{g.date ? new Date(g.date).toLocaleDateString() : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="card-apple p-8 text-center">
                  <p className="text-[#86868B]">{language === 'es' ? 'No hay notas registradas.' : 'No grades recorded yet.'}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
